import { FormLayout } from '../../../quest/program-wizard/common';
import { ProductDetailsProps } from '../../../archives/';
import { ClientContactInfo } from '../models/clientContactModel';
import { useDataPart, useStepContext } from '@/app/core/components/step-wizard';
import { ContactPicker, ContactDetails } from '@/app/features/products';
import { objectUtils } from '@/app/core/utils';
import { ModuleInputProps } from '@/app/features/products/common/components/input-props';
import { useState, useEffect, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ProductDetailsModel } from '@ssar-app/domain';
import { yupResolver } from '@hookform/resolvers';
import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Tooltip,
  Text,
  Box,
  Grid,
  GridItem,
  SimpleGrid,
} from '@chakra-ui/react';
import { Modal } from '@ssar-app/uikit';

export const ProductDetails = (
  props: ModuleInputProps<ProductDetailsProps>,
) => {
  const { stepKey, validator } = props;
  const { contactType, clientAttributes } = props.moduleProps;
  const [clientContactInfo, setClientContactInfo] = useState<
    ClientContactInfo[]
  >([]);
  const { isStepActive, onStepDeactivate } = useStepContext();
  const [dataPartState, setDataPartState] =
    useDataPart<ProductDetailsModel>(stepKey);

  onStepDeactivate(() => {
    return new Promise((_resolve, _reject) => {
      trigger([
        'programName',
        'clientName',
        'clientUserName',
        'clientUserEmail',
      ]).finally(() => {
        updateState();
      });
      _resolve(true);
    });
  });

  const [selectedContact, setSelectedContact] = useState<
    ClientContactInfo | undefined
  >(undefined);
  const [model, setModel] = useState<Partial<ProductDetailsModel>>(
    dataPartState.data || {},
  );
  const [clientModel, setClientModel] = useState<Partial<ProductDetailsModel>>(
    {
      ...dataPartState.data,
      clientName: '',
      clientUserName: '',
      clientUserEmail: '',
    } || {},
  );
  const [isValidForm, setIsValidForm] = useState<boolean>(false);

  const formMethods = useForm<ProductDetailsModel>({
    defaultValues: clientModel || {},
    mode: 'onChange',
    resolver: yupResolver(validator),
  });
  const { errors, control, formState, trigger, setValue } = formMethods;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const confirmToUpdateContact = (onOk: (...args: any[]) => any): void => {
    Modal.confirm({
      header: 'Do you want to continue?',
      headerProps: { pb: 4 },
      bodyProps: { pt: 0 },
      content:
        'You are about to overwrite the contact details with the selected contact. Do you want to continue?',
      onOk,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onCancel() {},
    });
  };

  const updateState = useCallback(() => {
    setDataPartState((currentState) => {
      const newState = {
        ...currentState,
        isValid: isValidForm,
        isDirty: formState.isDirty,
        isValidAsDraft: model.programName
          ? model.programName?.length > 0
          : false,
        data: {
          ...currentState.data,
          ...model,
        },
      };
      return newState;
    });
  }, [formState, model, setDataPartState, isValidForm]);

  useEffect(() => {
    if (clientModel && validator) {
      validator
        .validate(clientModel)
        .then(() => {
          setIsValidForm(true);
        })
        .catch((_error) => {
          setIsValidForm(false);
        });
    }
  }, [clientModel, validator]);

  useEffect(() => {
    updateState();
  }, [isValidForm, updateState]);

  const updateValues = (newContact: ClientContactInfo | undefined) => {
    setValue('clientName', newContact?.clientName || '', {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue('clientUserName', newContact?.clientUserName || '', {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue('clientUserEmail', newContact?.clientUserEmail || '', {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const updateClientDetails = (newContact: ClientContactInfo | undefined) => {
    updateValues(newContact);
    setSelectedContact(newContact);
    if (newContact) {
      const newDetails = {
        ...model,
        ...newContact,
      };
      setModel(newDetails);
      setClientModel(newDetails);
    }
  };

  useEffect(() => {
    if (clientAttributes) {
      const clientsInfo = clientAttributes.contacts.map((ct) => {
        return {
          recordType: ct.recordType,
          contactType: ct.addressType,
          clientUserName: ct.contactName,
          clientName: ct.entityName,
          clientUserEmail: ct.contactEmail,
        };
      });
      setClientContactInfo(clientsInfo);
      const mappedClient = clientsInfo.filter(
        (ct) => dataPartState.data?.clientName === ct.clientName,
      );
      if (mappedClient.length > 0) {
        const newModel = {
          ...clientModel,
          ...mappedClient[0],
        };
        updateValues(mappedClient[0]);
        setClientModel(newModel);
        formMethods.reset(newModel);
        setSelectedContact(mappedClient[0]);
      } else {
        // updateValues(mappedClient[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientAttributes]);

  if (!isStepActive) {
    return null;
  }

  return (
    <Flex direction='column' w='full'>
      <form>
        <FormLayout header='Name & Account Number'>
          <SimpleGrid columns={1}>
            <Box>
              <FormControl isRequired isInvalid={!!errors.programName} mb={6}>
                <Grid templateColumns='repeat(4,1fr)'>
                  <GridItem justifyContent='end' display='flex'>
                    <FormLabel>Program Name :</FormLabel>
                  </GridItem>
                  <GridItem colSpan={3}>
                    <Controller
                      name='programName'
                      defaultValue={model?.programName || null}
                      control={control}
                      render={({ value, onChange }) => (
                        <Tooltip placement='top' label='Name of the Program'>
                          <Input
                            onChange={(e) => {
                              onChange(e.target.value);
                              setModel({
                                ...model,
                                programName: e.target.value,
                              });
                              setClientModel({
                                ...clientModel,
                                programName: e.target.value,
                              });
                            }}
                            value={value}
                            placeholder='Program Name'
                            defaultValue={model?.programName}
                            spellCheck={false}
                          />
                        </Tooltip>
                      )}
                    ></Controller>
                    <FormErrorMessage>
                      {errors.programName?.message}
                    </FormErrorMessage>
                    <FormHelperText fontSize='xs'>
                      Note: Program Name must be at least 3 characters for auto
                      saving the program as a Draft
                    </FormHelperText>
                  </GridItem>
                </Grid>
              </FormControl>
            </Box>
            <Box>
              <FormControl isInvalid={errors.accountNumber ? true : false}>
                <Grid templateColumns='repeat(4,1fr)'>
                  <GridItem justifyContent='end' display='flex'>
                    <FormLabel>Account Number :</FormLabel>
                  </GridItem>
                  <GridItem colSpan={3}>
                    <Controller
                      name='accountNumber'
                      defaultValue={model?.accountNumber || null}
                      control={control}
                      render={({ value }) => <label>{value}</label>}
                    ></Controller>
                    <FormErrorMessage>
                      {errors.accountNumber?.message}
                    </FormErrorMessage>
                  </GridItem>
                </Grid>
              </FormControl>
            </Box>
          </SimpleGrid>
        </FormLayout>
        <FormLayout
          header={
            <Flex direction='row' alignItems='baseline'>
              <Text fontSize='md' mr={6}>
                Client Details
              </Text>
              <ContactPicker
                inputContact={selectedContact}
                onContactSelected={(newContact) => {
                  const updateContact = (): void => {
                    updateClientDetails(newContact);
                  };
                  if (objectUtils.areAllPropsEmpty(selectedContact || {})) {
                    updateContact();
                  } else if (
                    !objectUtils.areEqual(selectedContact, newContact, '_id')
                  ) {
                    confirmToUpdateContact(updateContact);
                  } else {
                    updateContact();
                  }
                }}
                contacts={clientContactInfo}
                contactType={contactType}
              ></ContactPicker>
            </Flex>
          }
        >
          <SimpleGrid columns={1}>
            <Box>
              <ContactDetails
                isReadOnly={true}
                control={control}
                errors={errors}
                model={clientModel}
              />
            </Box>
          </SimpleGrid>
        </FormLayout>
      </form>
    </Flex>
  );
};
